import { useEffect, useMemo, useState } from 'react';
import ZoomableMap from './ZoomableMap';
import { geoIdentity, geoPath } from 'd3-geo';
import axios from 'axios';
import zhConvertor from 'zhconvertor';
import johorGeo from './johor/johor_dun.json';
import './johor/johor.css';

// Alliance colours come from getInfo (canonical alliances.color_hex) per seat.

// Hardcoded district names. Chinese is stored traditional (繁體) and converted
// to simplified by tr() unless the site language is zh_TW — matching the rest of
// SeatDetail (e.g. SeatInfoPanel.jsx). English is the romanised Malay name.
const SEAT_NAMES = {
  N1: { c: '巫羅加什', e: 'Buloh Kasap' },
  N2: { c: '利民達', e: 'Jementah' },
  N3: { c: '柏瑪尼斯', e: 'Pemanis' },
  N4: { c: '哥美拉', e: 'Kemerlah' },
  N5: { c: '丁能', e: 'Tenang' },
  N6: { c: '彼咯', e: 'Bekok' },
  N7: { c: '武吉哈逢', e: 'Bukit Kepong' },
  N8: { c: '武吉巴西', e: 'Bukit Pasir' },
  N9: { c: '甘蜜', e: 'Gambir' },
  N10: { c: '東甲', e: 'Tangkak' },
  N11: { c: '實廊', e: 'Serom' },
  N12: { c: '文打煙', e: 'Bentayan' },
  N13: { c: '新邦二南', e: 'Simpang Jeram' },
  N14: { c: '武吉南寧', e: 'Bukit Naning' },
  N15: { c: '馬哈拉尼', e: 'Maharani' },
  N16: { c: '雙溪峇浪', e: 'Sungai Balang' },
  N17: { c: '聖模那', e: 'Semerah' },
  N18: { c: '鐵山', e: 'Sri Medan' },
  N19: { c: '永平', e: 'Yong Peng' },
  N20: { c: '實瑪廊', e: 'Semarang' },
  N21: { c: '巴力安尼', e: 'Parit Yaani' },
  N22: { c: '巴力拉惹', e: 'Parit Raja' },
  N23: { c: '帆加蘭', e: 'Penggaram' },
  N24: { c: '新加蘭', e: 'Senggarang' },
  N25: { c: '龍引', e: 'Rengit' },
  N26: { c: '嗎什', e: 'Macap' },
  N27: { c: '拉央拉央', e: 'Layang-Layang' },
  N28: { c: '明吉摩', e: 'Mengkibol' },
  N29: { c: '馬哥打', e: 'Mahkota' },
  N30: { c: '巴羅', e: 'Paloh' },
  N31: { c: '加亨', e: 'Kahang' },
  N32: { c: '興樓', e: 'Endau' },
  N33: { c: '丁加洛', e: 'Tenggaroh' },
  N34: { c: '班底', e: 'Panti' },
  N35: { c: '巴西拉惹', e: 'Pasir Raja' },
  N36: { c: '素里里', e: 'Sedili' },
  N37: { c: '舊柔佛', e: 'Johor Lama' },
  N38: { c: '本那哇', e: 'Penawar' },
  N39: { c: '丹絨蘇叻', e: 'Tanjung Surat' },
  N40: { c: '地南', e: 'Tiram' },
  N41: { c: '優景鎮', e: 'Puteri Wangsa' },
  N42: { c: '柔佛再也', e: 'Johor Jaya' },
  N43: { c: '百萬鎮', e: 'Permas' },
  N44: { c: '拉慶', e: 'Larkin' },
  N45: { c: '士都蘭', e: 'Stulang' },
  N46: { c: '柏伶', e: 'Perling' },
  N47: { c: '甘拔士', e: 'Kempas' },
  N48: { c: '士姑來', e: 'Skudai' },
  N49: { c: '依斯干達城', e: 'Kota Iskandar' },
  N50: { c: '武吉柏邁', e: 'Bukit Permai' },
  N51: { c: '武吉峇都', e: 'Bukit Batu' },
  N52: { c: '士乃', e: 'Senai' },
  N53: { c: '文律', e: 'Benut' },
  N54: { c: '埔萊士巴當', e: 'Pulai Sebatang' },
  N55: { c: '北干那那', e: 'Pekan Nenas' },
  N56: { c: '龜咯', e: 'Kukup' },
};

const tr = (text) =>
  localStorage.getItem('lang') !== 'zh_TW' ? zhConvertor.t2s(text) : text;

const cnName = (seat) => (SEAT_NAMES[seat] ? tr(SEAT_NAMES[seat].c) : seat);
const enName = (seat) => (SEAT_NAMES[seat] ? SEAT_NAMES[seat].e : '');

// Default fill for a district with no result yet.
const DEFAULT_FILL = '#e2e6ee';
// Seam between districts.
const SEAM = '#ffffff';

// 2.5D layout. Johor is landscape (~1.32 aspect); leave DEPTH px at the bottom
// of the viewBox for the extruded side walls + a little for the shadow.
const W = 900;
const H = 700;
const PAD = 26;
const DEPTH = 22;        // extrusion thickness (uniform)
const WALL_DX = 3;       // light comes from upper-left -> walls drop down-right

// Crowded Johor Bahru districts whose centroid labels overlap. Their number
// labels are pulled onto a horizontal shelf in the empty sea below the southern
// coast and joined back to the district by a leader line. Slots are sorted by
// centroid x at render time so the leader lines never cross each other.
const CALLOUT_SEATS = ['N41', 'N42', 'N43', 'N44', 'N45', 'N46', 'N47', 'N48'];
const CALLOUT_SET = new Set(CALLOUT_SEATS);
const SHELF_Y = 660;     // y of the callout label row
const SHELF_X0 = 445;    // shelf left / right extent
const SHELF_X1 = 700;
const PILL_W = 32;
const PILL_H = 18;

// Darken a #rrggbb colour towards black by `amount` (0..1) for the side walls.
const shade = (hex, amount = 0.4) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#9aa0ad';
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 255) * (1 - amount));
  const g = Math.round(((n >> 8) & 255) * (1 - amount));
  const b = Math.round((n & 255) * (1 - amount));
  return `rgb(${r}, ${g}, ${b})`;
};

const Johor = (props) => {
  const { state, code, lastElection, static: isStatic, onSeatClick, fills } = props;

  // Highlight shared between the map and the name grid (purely local — locating
  // a district from its list row and vice-versa).
  const [activeSeat, setActiveSeat] = useState(null);

  // Project the GeoJSON once -> a top-face path string + label centroid per
  // district.
  const districts = useMemo(() => {
    // Planar projection (lon/lat treated as x/y, Y flipped for SVG), matching
    // MalaysiaMap.jsx. Avoids d3-geo's spherical winding rule, which would
    // otherwise render each district as the giant complement of its true shape.
    const projection = geoIdentity().reflectY(true).fitExtent(
      [[PAD, PAD], [W - PAD, H - PAD - DEPTH]],
      johorGeo,
    );
    const path = geoPath(projection);
    return johorGeo.features
      .map((f) => {
        const [cx, cy] = path.centroid(f);
        return { seat: f.properties.seat, d: path(f), cx, cy };
      })
      .filter((dist) => dist.d);
  }, []);

  // Leader-line callouts for the crowded southern cluster: each callout seat's
  // label is laid out left-to-right (by centroid x) along the shelf, so the
  // lines fan out without crossing.
  const callouts = useMemo(() => {
    const items = districts
      .filter((d) => CALLOUT_SET.has(d.seat))
      .sort((a, b) => a.cx - b.cx);
    const n = items.length;
    return items.map((d, i) => ({
      seat: d.seat,
      cx: d.cx,
      cy: d.cy,
      sx: n <= 1 ? (SHELF_X0 + SHELF_X1) / 2 : SHELF_X0 + ((SHELF_X1 - SHELF_X0) * i) / (n - 1),
      sy: SHELF_Y,
    }));
  }, [districts]);

  // Poll the results API (same contract as before) — but only when the parent
  // didn't supply `fills` (e.g. SeatsDetail derives colours from its own
  // seatinfo poll, so this component must not issue a duplicate getInfo poll).
  const [maps, setMaps] = useState([]);
  const hasFills = fills != null;
  useEffect(() => {
    if (hasFills) return undefined;
    let active = true;
    const callMap = () => {
      const url = `public-api/getInfo?state=${state}&code=${code}${lastElection ? '&last_election=true' : ''}`;
      axios
        .get(url)
        .then((res) => { if (active) setMaps(Array.isArray(res.data) ? res.data : []); })
        .catch((err) => console.log(err));
    };
    callMap();
    const interval = setInterval(callMap, 15000);
    return () => {
      active = false;
      clearInterval(interval);
      setMaps([]);
    };
  }, [state, code, lastElection, hasFills]);

  // seat -> winning alliance colour. Empty maps (e.g. current election with no
  // results yet) naturally falls back to DEFAULT_FILL, so colours never get
  // stuck when toggling 上届/本届.
  const fillBySeat = useMemo(() => {
    if (fills != null) return fills;
    const out = {};
    for (let i = 0; i < maps.length; i += 1) {
      const c = maps[i].color;
      if (c) out[maps[i].seat] = c;
    }
    return out;
  }, [maps, fills]);

  const handleClick = (seat) => {
    if (onSeatClick) onSeatClick(seat);
  };
  const labelOf = (seat) => `${seat} ${cnName(seat)}${enName(seat) ? ` · ${enName(seat)}` : ''}`;

  const svg = (
    <svg
      id="state-map"
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <filter id="johor-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="7" stdDeviation="7" floodColor="#1c2330" floodOpacity="0.28" />
        </filter>
      </defs>

      {/* Extruded side walls: each district's dark twin, offset down-right and
          drawn behind the top faces. Adjacent tiles hide interior walls, so the
          state reads as one solid raised slab. */}
      <g transform={`translate(${WALL_DX}, ${DEPTH})`} filter="url(#johor-shadow)">
        {districts.map((dist) => (
          <path
            key={`wall-${dist.seat}`}
            d={dist.d}
            fill={shade(fillBySeat[dist.seat] || DEFAULT_FILL, 0.45)}
          />
        ))}
      </g>

      {/* Top faces: colour, seam stroke, interactivity. id={seat} preserves the
          existing `.sabah_map_clickable path[id^="N"]:hover` brightness effect. */}
      <g>
        {districts.map((dist) => {
          const isActive = activeSeat === dist.seat;
          return (
            <path
              key={dist.seat}
              id={dist.seat}
              className={`johor-tile ${isActive ? 'is-active' : ''}`}
              d={dist.d}
              fill={fillBySeat[dist.seat] || DEFAULT_FILL}
              stroke={isActive ? '#11161f' : SEAM}
              strokeWidth={isActive ? 2.6 : 1.4}
              strokeLinejoin="round"
              tabIndex={onSeatClick ? 0 : undefined}
              onClick={onSeatClick ? () => handleClick(dist.seat) : undefined}
              onMouseEnter={() => setActiveSeat(dist.seat)}
              onMouseLeave={() => setActiveSeat((s) => (s === dist.seat ? null : s))}
              onFocus={() => setActiveSeat(dist.seat)}
              onBlur={() => setActiveSeat((s) => (s === dist.seat ? null : s))}
              onKeyDown={onSeatClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(dist.seat); }
              } : undefined}
              aria-label={labelOf(dist.seat)}
            >
              <title>{labelOf(dist.seat)}</title>
            </path>
          );
        })}
      </g>

      {/* Inline seat-number labels at each centroid (pointer-events:none so taps
          fall through to the district). Crowded-cluster seats are handled by the
          callout layer below instead. Skipped on the tiny static render. */}
      {!isStatic && (
        <g className="johor-labels">
          {districts.filter((dist) => !CALLOUT_SET.has(dist.seat)).map((dist) => (
            <text
              key={`lbl-${dist.seat}`}
              x={dist.cx}
              y={dist.cy}
              className="johor-label"
              textAnchor="middle"
              dominantBaseline="central"
            >
              {dist.seat}
            </text>
          ))}
        </g>
      )}

      {/* Leader-line callouts: pull each crowded district's label onto the shelf
          below the coast. The pill is a real tap target (the tiny districts are
          nearly impossible to tap) and syncs the shared activeSeat highlight. */}
      {!isStatic && (
        <g className="johor-callouts">
          {callouts.map((c) => {
            const isActive = activeSeat === c.seat;
            return (
              <g key={`call-${c.seat}`} className={`johor-callout ${isActive ? 'is-active' : ''}`}>
                <line
                  className="johor-callout-line"
                  x1={c.cx}
                  y1={c.cy}
                  x2={c.sx}
                  y2={c.sy - PILL_H / 2}
                />
                <circle className="johor-callout-dot" cx={c.cx} cy={c.cy} r={2.6} />
                <g
                  className="johor-callout-pill-group"
                  role={onSeatClick ? 'button' : undefined}
                  tabIndex={onSeatClick ? 0 : undefined}
                  onClick={onSeatClick ? () => handleClick(c.seat) : undefined}
                  onMouseEnter={() => setActiveSeat(c.seat)}
                  onMouseLeave={() => setActiveSeat((s) => (s === c.seat ? null : s))}
                  onFocus={() => setActiveSeat(c.seat)}
                  onBlur={() => setActiveSeat((s) => (s === c.seat ? null : s))}
                  onKeyDown={onSeatClick ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(c.seat); }
                  } : undefined}
                  aria-label={labelOf(c.seat)}
                >
                  <rect
                    className="johor-callout-pill"
                    x={c.sx - PILL_W / 2}
                    y={c.sy - PILL_H / 2}
                    width={PILL_W}
                    height={PILL_H}
                    rx={PILL_H / 2}
                  />
                  <text
                    className="johor-callout-text"
                    x={c.sx}
                    y={c.sy}
                    textAnchor="middle"
                    dominantBaseline="central"
                  >
                    {c.seat}
                  </text>
                  <title>{labelOf(c.seat)}</title>
                </g>
              </g>
            );
          })}
        </g>
      )}
    </svg>
  );

  if (isStatic) {
    return <div className="mapinner">{svg}</div>;
  }

  return (
    <ZoomableMap contentClassName="mapinner sabah_map_clickable">{svg}</ZoomableMap>
  );
};

export default Johor;
